import { useState, useCallback, useEffect } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import {
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  Checkbox,
  ListItemText,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  FormHelperText,
} from '@mui/material';

const DisabilitySelector = ({
  isDisabled,
  disabilities,
  isDisabledOptions = [],
  disabilityOptions = [],
}: {
  isDisabled: string;
  disabilities: string[];
  isDisabledOptions: string[];
  disabilityOptions: string[];
}) => {
  const {
    control,
    formState: { errors },
    setValue,
    trigger,
    clearErrors,
  } = useFormContext();
  const [selectedDisabilities, setSelectedDisabilities] =
    useState(disabilities);
  const [hasDisability, setHasDisability] = useState(
    isDisabled === isDisabledOptions[2],
  );

  useEffect(() => {
    if (!disabilities || disabilities.length === 0) setValue('Disability', []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Trigger validation on mount if user has disability but none selected
  useEffect(() => {
    if (hasDisability && selectedDisabilities.length === 0) {
      trigger('Disability');
    }
  }, [hasDisability, selectedDisabilities.length, trigger]);

  const onDisabilityCheck = (selectedValue: string) => async () => {
    const currentIndex = selectedDisabilities.indexOf(selectedValue);
    const newSelected = [...selectedDisabilities];

    if (currentIndex === -1) {
      newSelected.push(selectedValue);
    } else {
      newSelected.splice(currentIndex, 1);
    }

    setValue('Disability', [...newSelected]);
    setSelectedDisabilities(newSelected);
    // Trigger validation to update form state
    await trigger('Disability');
  };

  const onIsDisabledChange = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      if (event.target.value === isDisabledOptions[2]) {
        setHasDisability(true);
        setValue('IsDisabled', event.target.value);
        // Trigger validation for Disability field
        await trigger('Disability');
        return;
      }

      setValue('IsDisabled', event.target.value);
      setHasDisability(false);
      setSelectedDisabilities([]);
      setValue('Disability', []);
      // Clear validation error when disability is no longer required
      clearErrors('Disability');
    },
    [isDisabledOptions, setValue, trigger, clearErrors],
  );

  return (
    <FormControl sx={{ width: '100%' }}>
      <Controller
        name="IsDisabled"
        control={control}
        rules={{ required: 'required' }}
        defaultValue={isDisabled}
        render={() => (
          <>
            <FormLabel
              sx={{
                fontSize: '14px',
                color: (theme) => {
                  if (!!errors['IsDisabled']) return theme.palette.error.main;
                },
              }}
            >
              Do you consider yourself to have a disability? *
            </FormLabel>
            <FormHelperText error={!!errors['IsDisabled']}>
              {errors['IsDisabled']?.message as string}
            </FormHelperText>
            <RadioGroup onChange={onIsDisabledChange} defaultValue={isDisabled}>
              {isDisabledOptions.map((option, index) => (
                <FormControlLabel
                  key={`radio${index + 1}`}
                  value={option}
                  control={<Radio />}
                  label={option}
                />
              ))}
            </RadioGroup>
          </>
        )}
      />
      {hasDisability && (
        <Controller
          name="Disability"
          control={control}
          defaultValue={disabilities || []}
          rules={{
            validate: (value) =>
              (value && value.length > 0) ||
              'Please select at least one disability option below',
          }}
          render={() => (
            <>
              <FormHelperText error={!!errors['Disability']}>
                {errors['Disability']?.message as string}
              </FormHelperText>
              <List
                sx={{
                  width: '100%',
                  maxWidth: 360,
                  bgcolor: 'background.paper',
                }}
              >
                {disabilityOptions.map((disability: string, index: number) => {
                  const labelId = `checkbox-list-label-${index}`;

                  return (
                    <ListItem key={`disability${index + 1}`} disablePadding>
                      <ListItemButton
                        role={undefined}
                        onClick={onDisabilityCheck(disability)}
                        dense
                      >
                        <ListItemIcon>
                          <Checkbox
                            edge="start"
                            checked={
                              selectedDisabilities.indexOf(disability) !== -1
                            }
                            tabIndex={-1}
                            disableRipple
                            inputProps={{ 'aria-label': labelId }}
                          />
                        </ListItemIcon>
                        <ListItemText id={labelId} primary={disability} />
                      </ListItemButton>
                    </ListItem>
                  );
                })}
              </List>
            </>
          )}
        />
      )}
    </FormControl>
  );
};

export default DisabilitySelector;
